import * as mutation from "./mutation-types";
import * as action from "./action-types";
import * as systemLogMutation from "@/store/system-logs/mutation-types";
import * as lcMutation from "@/store/levy-collections/mutation-types";
import * as lcpMutation from "@/store/levy-collection-providers/mutation-types";
import * as levyEstimatorMutation from "@/store/levy-estimator/mutation-types";
import * as invoiceMutation from "@/store/invoices/mutation-types";
import * as noticeMutation from "@/store/notices/mutation-types";
import * as dashboardMutation from "@/store/dashboard/mutation-types";
import { SHOW_NOTIFICATION } from "@/store/notifications/mutation-types";
import { GLOBAL_ERROR } from "@/store/system/mutation-types";
import { deepCopyAxiosException } from "@/store/helpers";
import { tableHelper } from "@/helpers/table.helper";
import axios from "axios";
import store from "@/store";

export const actions = {
  async [action.LIST]({ commit, state }) {
    commit(mutation.LIST_STARTED);
    try {
      const url = tableHelper.constructUrl(
        `/api/levy-collections/${state.listing.levyCollectionId}/providers`,
        state.listing.paging,
        state.listing.criteria
      );
      const { headers, data } = await axios.get(url);
      commit(mutation.LIST_SET_CRITERIA_DESCRIPTION, tableHelper.getCriteriaInfo(headers));
      commit(mutation.LIST_SET_PAGING, tableHelper.getPagingFromHeaders(headers));
      commit(mutation.LIST_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.LIST_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.LIST_NEXT_PAGE]({ state, commit, dispatch }) {
    commit(mutation.LIST_SET_PAGING, { page: state.listing.paging.page + 1 });
    await dispatch(action.LIST);
  },
  async [action.LIST_PREVIOUS_PAGE]({ state, commit, dispatch }) {
    commit(mutation.LIST_SET_PAGING, { page: state.listing.paging.page - 1 });
    await dispatch(action.LIST);
  },
  async [action.LIST_REFRESH]({ commit, state, dispatch }, levyCollectionId) {
    const needsRefreshed =
      (!state.listing.loaded && !state.listing.loading) || state.listing.levyCollectionId != levyCollectionId;
    if (needsRefreshed) {
      commit(mutation.LIST_SET_FILTERS, { levyCollectionId });
      await dispatch(action.LIST);
    }
  },

  async [action.EXPORT]({ commit, state }) {
    commit(mutation.EXPORT_STARTED);
    try {
      await axios.get(
        tableHelper.constructUrl(
          `/api/exports/validate/levy-collections/${state.listing.levyCollectionId}/providers`,
          state.listing.criteria
        )
      );
      commit(SHOW_NOTIFICATION, "Your export will commence shortly.");
      const url = tableHelper.constructUrl(
        `/api/exports/levy-collections/${state.listing.levyCollectionId}/providers`,
        state.listing.criteria
      );
      tableHelper.downloadExportedSpreadsheet(url);
      commit(mutation.EXPORT_SUCCEEDED);
      return true;
    } catch (e) {
      commit(mutation.EXPORT_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.VIEW]({ commit, state }, { levyCollectionId, providerId }) {
    commit(mutation.VIEW_STARTED, { levyCollectionId, providerId });
    try {
      const url = `/api/levy-collections/${levyCollectionId}/providers/${providerId}`;
      const { data } = await axios.get(url);
      commit(mutation.VIEW_SUCCEEDED, data);
      const index = tableHelper.getIndexOfId(state.listing, state.view.data.id);
      commit(mutation.VIEW_NEXT, tableHelper.getNextRecordInfo(index, state.listing, "providerId"));
      commit(mutation.VIEW_PREVIOUS, tableHelper.getPreviousRecordInfo(index, state.listing, "providerId"));
      return true;
    } catch (e) {
      commit(mutation.VIEW_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.VIEW_REFRESH]({ state, dispatch }, { levyCollectionId, providerId }) {
    const needsRefreshed =
      (!state.view.loaded && !state.view.loading) ||
      state.view.levyCollectionId != levyCollectionId ||
      state.view.providerId != providerId;
    if (needsRefreshed) {
      await dispatch(action.VIEW, { levyCollectionId, providerId });
    }
  },

  async [action.WARNINGS_GET]({ commit }, levyCollectionId) {
    commit(mutation.WARNINGS_GET_STARTED, levyCollectionId);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/warnings`;
      const { data } = await axios.get(url);
      commit(mutation.WARNINGS_GET_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.WARNINGS_GET_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.WARNINGS_REFRESH]({ state, dispatch }, levyCollectionId) {
    const needsRefreshed =
      (!state.warnings.loaded && !state.warnings.loading) || state.warnings.levyCollectionId != levyCollectionId;
    if (needsRefreshed) {
      await dispatch(action.WARNINGS_GET, levyCollectionId);
    }
  },

  async [action.EXISTS]({ commit }, { levyCollectionId, providerId }) {
    commit(mutation.EXISTS_STARTED, { levyCollectionId, providerId });
    try {
      const url = `/api/levy-collections/${levyCollectionId}/providers/${providerId}`;
      await axios.get(url);
      commit(mutation.EXISTS_SUCCEEDED, true);
      return true;
    } catch (e) {
      if (e && e.response && e.response.status == 404) {
        commit(mutation.EXISTS_SUCCEEDED, false);
        return true;
      } else {
        commit(mutation.EXISTS_FAILED);
        commit(GLOBAL_ERROR, deepCopyAxiosException(e));
        return false;
      }
    }
  },

  async [action.LIST_FOR_PROVIDER]({ commit }, providerId) {
    commit(mutation.LIST_FOR_PROVIDER_STARTED, providerId);
    try {
      const url = `/api/providers/${providerId}/levy-collection-providers`;
      const { data } = await axios.get(url);
      commit(mutation.LIST_FOR_PROVIDER_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.LIST_FOR_PROVIDER_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.ASSIGNED_PAYMENT_STATUS_SAVE]({ commit, state }, { levyCollectionId, providerId }) {
    commit(mutation.ASSIGNED_PAYMENT_STATUS_SAVE_STARTED);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/providers/${providerId}/assigned-payment-status`;
      await axios.put(url, state.assignedPaymentStatus.data);
      commit(mutation.STATE_UNLOAD);
      commit(dashboardMutation.STATE_UNLOAD);
      commit(systemLogMutation.STATE_UNLOAD);
      commit(noticeMutation.STATE_UNLOAD);
      commit(invoiceMutation.STATE_UNLOAD);
      commit(mutation.ASSIGNED_PAYMENT_STATUS_SAVE_SUCCEEDED);
      commit(SHOW_NOTIFICATION, "Set payment status.");
      return true;
    } catch (e) {
      commit(mutation.ASSIGNED_PAYMENT_STATUS_SAVE_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.TASKS_GET]({ commit }, { levyCollectionId, providerId }) {
    commit(mutation.TASKS_GET_STARTED, { levyCollectionId, providerId });
    try {
      const url = `/api/levy-collections/${levyCollectionId}/providers/${providerId}/tasks`;
      const { data } = await axios.get(url);
      commit(mutation.TASKS_GET_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.TASKS_GET_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.TASKS_REFRESH]({ state, dispatch }, { levyCollectionId, providerId }) {
    const reloadRequired =
      (!state.tasks.loaded && !state.tasks.loading) ||
      state.tasks.levyCollectionId != levyCollectionId ||
      state.tasks.providerId != providerId;
    if (reloadRequired) {
      await dispatch(action.TASKS_GET, { levyCollectionId, providerId });
    }
  },

  async [action.TASK_GET_PROVIDERS]({ commit }, { levyCollectionId, taskType }) {
    commit(mutation.TASK_GET_PROVIDERS_STARTED);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/providers/for-task/${taskType}`;
      const { data } = await axios.get(url);
      commit(mutation.TASK_GET_PROVIDERS_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.TASK_GET_PROVIDERS_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.TASK_RUN]({ commit, dispatch }, { type, levyCollectionId, providerId }) {
    commit(mutation.TASK_RUN_STARTED, type);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/providers/${providerId}/run-task/${type}`;
      const { data } = await axios.post(url, { partOfBatch: false });
      dispatch(action.TASK_RUN_FINISH_UP);
      commit(mutation.TASK_RUN_SUCCEEDED, data);
      commit(
        SHOW_NOTIFICATION,
        `${store.state.lookups.data.providerTaskTypeDescriptions[type]} completed successfully.`
      );
      return true;
    } catch (e) {
      commit(mutation.TASK_RUN_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.FAVOURITE_QUIETLY]({ commit }, { type, levyCollectionId, providerId }) {
    commit(mutation.FAVOURITE_QUIETLY_STARTED);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/providers/${providerId}/run-task/${type}`;
      await axios.post(url, { partOfBatch: true });
      commit(mutation.FAVOURITE_QUIETLY_SUCCEEDED, { levyCollectionId, providerId, value: type === "Favourite" });
      return true;
    } catch (e) {
      commit(mutation.FAVOURITE_QUIETLY_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.TASK_RUN_QUIETLY]({ commit }, { type, levyCollectionId, providerId }) {
    commit(mutation.TASK_RUN_QUIETLY_STARTED);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/providers/${providerId}/run-task/${type}`;
      const { data } = await axios.post(url, { partOfBatch: true });
      commit(mutation.TASK_RUN_QUIETLY_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.TASK_RUN_QUIETLY_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.TASK_RUN_WITH_EXPLANATION]({ commit, dispatch }, { type, levyCollectionId, providerId, explanation }) {
    commit(mutation.TASK_RUN_STARTED);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/providers/${providerId}/run-task-with-explanation/${type}`;
      const { data } = await axios.post(url, { explanation });
      dispatch(action.TASK_RUN_FINISH_UP);
      commit(mutation.TASK_RUN_SUCCEEDED, data);
      commit(
        SHOW_NOTIFICATION,
        `${store.state.lookups.data.providerTaskTypeDescriptions[type]} completed successfully.`
      );
      return true;
    } catch (e) {
      commit(mutation.TASK_RUN_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.TASK_RUN_WITH_DUE_DATE]({ commit, dispatch }, { type, levyCollectionId, providerId, due }) {
    commit(mutation.TASK_RUN_STARTED);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/providers/${providerId}/run-task-with-due-date/${type}`;
      const { data } = await axios.post(url, { due });
      dispatch(action.TASK_RUN_FINISH_UP);
      commit(mutation.TASK_RUN_SUCCEEDED, data);
      commit(
        SHOW_NOTIFICATION,
        `${store.state.lookups.data.providerTaskTypeDescriptions[type]} completed successfully.`
      );
      return true;
    } catch (e) {
      commit(mutation.TASK_RUN_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },
  [action.TASK_RUN_FINISH_UP]({ commit }) {
    commit(mutation.STATE_UNLOAD);
    commit(lcMutation.STATE_UNLOAD);
    commit(systemLogMutation.STATE_UNLOAD);
    commit(dashboardMutation.STATE_UNLOAD);
    commit(lcpMutation.STATE_UNLOAD);
    commit(levyEstimatorMutation.STATE_UNLOAD);
    commit(noticeMutation.STATE_UNLOAD);
    commit(invoiceMutation.STATE_UNLOAD);
  },

  async [action.UPLOAD_SPREADSHEET]({ commit, state }, { formData, endpointSuffix }) {
    const s = state.providerUpload;
    commit(mutation.UPLOAD_SPREADSHEET_STARTED);
    try {
      const action = `upload-${endpointSuffix}${s.isDryRun ? "-dry-run" : ""}`;
      const url = `/api/levy-collections/${s.levyCollectionId}/providers/${action}`;
      const { data } = await axios.post(url, formData);
      if (!s.isDryRun) {
        commit(mutation.STATE_UNLOAD);
        commit(systemLogMutation.STATE_UNLOAD);
        commit(dashboardMutation.STATE_UNLOAD);
        commit(lcMutation.STATE_UNLOAD);
        commit(lcpMutation.STATE_UNLOAD);
        commit(levyEstimatorMutation.STATE_UNLOAD);
        commit(SHOW_NOTIFICATION, "Spreadsheet was uploaded.");
      }
      commit(mutation.UPLOAD_SPREADSHEET_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.UPLOAD_SPREADSHEET_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.UPLOAD_GET_TEMPLATE]({ commit }, { levyCollectionId, endpointSuffix, usePrevious }) {
    commit(mutation.UPLOAD_GET_TEMPLATE_STARTED);
    try {
      commit(SHOW_NOTIFICATION, "Your download will commence shortly.");
      const url = `/api/levy-collections/${levyCollectionId}/providers/upload-${endpointSuffix}?usePrevious=${usePrevious}`;
      tableHelper.downloadExportedSpreadsheet(url);
      commit(mutation.UPLOAD_GET_TEMPLATE_SUCCEEDED);
      return true;
    } catch (e) {
      commit(mutation.UPLOAD_GET_TEMPLATE_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.IN_SCOPE_PREVIEW_GET]({ commit }, levyCollectionId) {
    commit(mutation.IN_SCOPE_PREVIEW_GET_STARTED, levyCollectionId);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/providers/preview-in-scope`;
      const { data } = await axios.get(url);
      commit(mutation.IN_SCOPE_PREVIEW_GET_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.IN_SCOPE_PREVIEW_GET_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.IN_SCOPE_PREVIEW_REFRESH]({ state, dispatch }, levyCollectionId) {
    const needsRefreshed =
      state.inScopePreview.levyCollectionId != levyCollectionId ||
      (!state.inScopePreview.loaded && !state.inScopePreview.loading);
    if (needsRefreshed) {
      await dispatch(action.IN_SCOPE_PREVIEW_GET, levyCollectionId);
    }
  },

  async [action.DISMISS_SAP_RECONCILIATION_WARNING]({ commit }, { levyCollectionId, providerId }) {
    commit(mutation.DISMISS_SAP_RECONCILIATION_WARNING_STARTED);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/providers/${providerId}/dismiss-sap-reconciliation-warning`;
      await axios.put(url);
      commit(mutation.DISMISS_SAP_RECONCILIATION_WARNING_SUCCEEDED);
      commit(mutation.STATE_UNLOAD);
      commit(dashboardMutation.STATE_UNLOAD);
      commit(invoiceMutation.STATE_UNLOAD);
      return true;
    } catch (e) {
      commit(mutation.DISMISS_SAP_RECONCILIATION_WARNING_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.GET_STATUS_HISTORY]({ commit }, { levyCollectionId, providerId }) {
    commit(mutation.GET_STATUS_HISTORY_STARTED, { levyCollectionId, providerId });
    try {
      const url = `/api/levy-collections/${levyCollectionId}/providers/${providerId}/status-history`;
      const { data } = await axios.get(url);
      commit(mutation.GET_STATUS_HISTORY_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.GET_STATUS_HISTORY_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.GET_TIMELINE]({ commit }, { levyCollectionId, providerId }) {
    commit(mutation.GET_TIMELINE_STARTED, { levyCollectionId, providerId });
    try {
      const url = `/api/levy-collections/${levyCollectionId}/providers/${providerId}/timeline`;
      const { data } = await axios.get(url);
      commit(mutation.GET_TIMELINE_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.GET_TIMELINE_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.FINALISE_GET]({ commit }, { levyCollectionId, providerId }) {
    commit(mutation.FINALISE_GET_STARTED);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/providers/${providerId}/finalisation`;
      const { data } = await axios.get(url);
      commit(mutation.FINALISE_GET_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.FINALISE_GET_FAILED, deepCopyAxiosException(e));
    }
  },
  async [action.FINALISE_SAVE]({ commit, state }, { levyCollectionId, providerId }) {
    commit(mutation.FINALISE_SAVE_STARTED);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/providers/${providerId}/finalisation`;
      const data = state.finalisation.data;
      if (data.manuallyFinalised) {
        await axios.put(url, data);
      } else {
        await axios.post(url, data);
      }
      commit(mutation.FINALISE_SAVE_SUCCEEDED);
      commit(mutation.STATE_UNLOAD);
      commit(dashboardMutation.STATE_UNLOAD);
      return true;
    } catch (e) {
      commit(mutation.FINALISE_SAVE_FAILED, deepCopyAxiosException(e));
    }
  }
};
